import GameObject from 'GameObject/GameObject';
import Game from 'Game';
import BasePopupHandler from './BasePopupHandler';
import UIItem, { IImageData, ITextData } from 'Prototype/UI/UIItem';
import { TimerActions } from 'State/Timer/TimerActions';
import { TimerType } from '../../Timer/TimerTypes';
import BaseUISystem from '../BaseUISystem';
import UIImageComponent from 'Component/UI/Image';
import UITextComponent from 'Component/UI/Text';
import { PlayerActions } from 'State/Player/PlayerActions';
import { FBTrackingEvent } from 'Utility/Tracking/FBTrackingEvent';
import { resetEventHandlerData, track } from 'Utility/Tracking/Track';
import { FTUXTrackingEventName } from 'Utility/Tracking/TrackingEvent';
import { IGameState } from 'State/Game/IGameState';
import Armature from 'Prototype/Armature';
import ParticleEmitter from 'Prototype/Effect/ParticleEmitter';
import Point from 'Utility/Point';
import { Random } from 'Utility/Random';
import { FieldStateHelper } from 'State/Player/Fields/FieldStateHelper';
import { PopupID } from 'State/UI/PopupID';
import Renderable from 'Component/Renderable';
import { StartAnimationClip } from 'System/Animation/AnimationHelper';
import { Slide } from 'System/Animation/Clip/Slide';
import { FieldSector, WorldDataHelper } from 'Utility/WorldDataHelper';

export default class CropUnlockedHandler extends BasePopupHandler {
    private harvestEmitters: ParticleEmitter[] = null;
    private newFarmUnlock: boolean = false;
    private fieldSector: FieldSector;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.CROP_UNLOCK_MENU, uiSystem);
    }

    protected onCreate() {
        this.clipShow = 'ShowCropUnlock';

        this.underlayParams.layout.anchors = [0.5, 0.45, 0.5, 0.45];
        this.underlayParams.cacheKey = 'back_unlock_field';
        this.submitBtnParams.text.textLocalizationKey = 'Great';
        this.submitBtnParams.text.style = Game.style.getStyle('popup.dark_blue_btn');
        this.submitBtnParams.button.cacheKey = 'button_medium_blue';

        super.onCreate();
    }

    protected createPopup(params?: any): void {
        super.createPopup({
            buttons: [this.submitBtnParams, this.closeBtnParams],
            underlay: this.underlayParams,
        });

        if (this.harvestEmitters === null) {
            this.addHarvestParticleEffects();

            this.harvestEmitters.forEach((emitter: ParticleEmitter) =>
                setTimeout(emitter.start.bind(emitter), Random.int(0, 750))
            );
        }

        const blankImage = UIItem.createImage({ cacheKey: 'blank' });
        const armature = GameObject.Instantiate(Armature, {
            dragonbonesName: 'unlock_field_popup',
            armatureName: 'unlock_field_popup',
            playOnStart: true,
        });
        blankImage.addChild(armature);
        this.background.addChild(blankImage);

        this.background.addChild(this.submitBtn);
        this.background.addChild(this.closeBtn);

        this.title = Game.locale.getText('Congratulations');
        this.background.addChild(this.titleContainer.parent);

        const cropIcon: GameObject<PIXI.Container> = UIItem.createImage(this.cropIconParams);
        this.background.addChild(cropIcon);

        const text: GameObject = UIItem.createText(this.textParams.text);
        this.background.addChild(text);

        const farmText: GameObject = UIItem.createText(this.textParams.farmText);
        this.background.addChild(farmText);

        const henry: GameObject = UIItem.createImage(this.henryParams);
        henry.getComponent(Renderable).visible = false;
        this.element.addChild(henry);

        StartAnimationClip(
            new Slide(henry, {
                offsets: [520, 0, 520, 0],
                delay: 400,
                duration: 300,
                hidden: true,
            })
        );
    }

    // TODO: Do this on show instead of on create
    private addHarvestParticleEffects(): void {
        this.harvestEmitters = [];

        const textures: string[] = [];

        // TODO: use the id provided in onShow
        const lastFieldUnlocked = FieldStateHelper.getLastUnlockedFieldId(Game.store.getState());

        const field = WorldDataHelper.getFieldFromId(lastFieldUnlocked);

        textures.push(`${field.field}_harvest_map`);

        for (let i = 0; i < 2; i++) {
            const harvestParticleEmitter: ParticleEmitter = GameObject.Instantiate(ParticleEmitter, {
                textures,
                data: Game.resource.get('crop_unlock_particle_data'),
                scale: new Point(1.2, 1.2),
                x: 400,
                y: 0,
                elapsedTime: Date.now() + 500 * Math.random(),
            });
            this.harvestEmitters.push(harvestParticleEmitter);
            this.background.addChild(harvestParticleEmitter);
        }
    }

    public onShow(params?: { id: string; newFarmUnlock: boolean }) {
        super.onShow(params);

        this.newFarmUnlock = params.newFarmUnlock;

        // TODO: Should not do this. We should have a proper reference to a universal crop ID. No string manipulation.
        this.fieldSector = WorldDataHelper.getFieldFromId(params.id);

        this.background
            .find(this.cropIconParams.name)
            // TODO: Move concatenation to helper method.
            .getComponent(UIImageComponent).texture = `unlock_${this.fieldSector.field}`;

        this.background.find(this.textParams.text.name).getComponent(UITextComponent).text = Game.locale
            .getText('CropUnlocked')
            .replace('[x]', Game.locale.getText(this.fieldSector.field));

        this.background.find(this.textParams.farmText.name).getComponent(Renderable).visible = this.newFarmUnlock;
    }

    protected onSubmit(): void {
        super.onSubmit();
        this.updateHarvestTimer();
    }
    protected onClose(): void {
        super.onClose();
        this.updateHarvestTimer();
    }
    protected updateHarvestTimer(): void {
        if (this.harvestEmitters !== null) {
            this.harvestEmitters.forEach((emitter: ParticleEmitter) => {
                emitter.destroy();
            });

            this.harvestEmitters = null;
        }
        const state: IGameState = Game.store.getState();
        if (state.playerState.timers.get(TimerType.HARVEST) !== null) {
            TimerActions.updateTimer({
                timer: TimerType.HARVEST,
                time: null,
            });
            Game.scene.getActiveScene().emit('TransitionCrops');
        }
    }

    public onShown(): void {
        super.onShown();

        const state: IGameState = Game.store.getState();
        if (!state.playerState.FBEvents.includes(FBTrackingEvent.FTUE_13_CropsUnlockedShow)) {
            Game.sdk.gameTracking(FBTrackingEvent.FTUE_13_CropsUnlockedShow);
            PlayerActions.addFTUETrackingEvent(FBTrackingEvent.FTUE_13_CropsUnlockedShow);

            track('stepId', 13);
            track('stepName', FBTrackingEvent.FTUE_13_CropsUnlockedShow);
            resetEventHandlerData(FTUXTrackingEventName.ftux, Game.store.getState());
        }
    }

    protected henryParams: IImageData = {
        name: 'farm_henry',
        cacheKey: 'henry_farm_unlocks',
        layout: {
            anchors: [0.5, 1, 0.5, 1],
            pivot: [0.5, 1],
            offsets: [520, -600, 520, -600],
            scale: [1, 1],
        },
    };
    protected cropIconParams: IImageData = {
        name: 'crop_icon',
        cacheKey: 'blank',
        layout: {
            anchors: [0.5, 0.52, 0.5, 0.52],
            pivot: [0.5, 0.5],
            offsets: [0, 50, 0, 0],
            scale: [1, 1],
            constrains: {
                widthMax: 299,
                widthMin: 299,
                heightMax: 360,
                heightMin: 360,
            },
        },
    };
    protected cropIconBgParams: IImageData = {
        name: 'crop_icon_background',
        cacheKey: 'crop_bg_large',
        layout: {
            anchors: [0.5, 0.46, 0.5, 0.46],
            pivot: [0.5, 0.5],
            offsets: [0, 0, 0, 0],
            scale: [1, 1],
        },
    };
    protected textParams: {
        text: ITextData;
        farmText: ITextData;
    } = {
        text: {
            name: 'field_unlocked_txt',
            textLocalizationKey: 'fieldUnlocked',
            style: {
                font: 'FrancoisOne',
                fill: '#bb3c2f',
                fontSize: '35px',
            },
            bounds: [650, 100],
            layout: {
                anchors: [0.5, 0.72, 0.5, 0.72],
                offsets: [0, 0, 0, 0],
                pivot: [0.5, 0.5],
            },
        },
        farmText: {
            name: 'field_unlocked_farm_txt',
            textLocalizationKey: 'CropAvailableInFarm',
            style: {
                font: 'FrancoisOne',
                fill: '#bb3c2f',
                fontSize: '35px',
            },
            bounds: [650, 100],
            layout: {
                anchors: [0.5, 0.78, 0.5, 0.78],
                offsets: [0, 0, 0, 0],
                pivot: [0.5, 0.5],
            },
        },
    };
}
